import {
  DateField,
  ExportButton,
  List,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useTable,
} from "@refinedev/antd";

import {
  Alert,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  FormProps,
  Input,
  Row,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  CrudFilters,
  IResourceComponentsProps,
  getDefaultFilter,
  useExport,
} from "@refinedev/core";

import dayjs from "dayjs";
import { IOrderReport } from "interfaces";
import React from "react";

export const OrderReportList: React.FC<IResourceComponentsProps> = () => {
  const [dateSelected, setDateSelected] = React.useState<boolean>(false);

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IOrderReport>({
      resource: "order-report",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { invoiceDate, vesselName, imoNumber, shipcoName, orderedBy } = params;

        if (invoiceDate) {
          const startDate = invoiceDate[0].startOf("day").toISOString();
          const endDate = invoiceDate[1].endOf("day").toISOString();

          filters.push({
            field: "invoiceDate",
            operator: "gte",
            value: startDate,
          });

          filters.push({
            field: "invoiceDate",
            operator: "lte",
            value: endDate,
          });
        }

        filters.push({
          field: "vesselName",
          operator: "contains",
          value: vesselName ? vesselName : undefined,
        });

        filters.push({
          field: "imoNumber",
          operator: "eq",
          value: imoNumber ? imoNumber : undefined,
        });

        filters.push({
          field: "shipcoName",
          operator: "contains",
          value: shipcoName ? shipcoName : undefined,
        });

        filters.push({
          field: "orderedBy",
          operator: "contains",
          value: orderedBy ? orderedBy : undefined,
        });

        return filters;
      },

      sorters: {
        initial: [
          {
            field: "invoiceDate",
            order: "desc",
          },
        ],
      },
    });

  const { triggerExport, isLoading: exportLoading } = useExport<IOrderReport>({
    resource: "order-report",
    filters: filters || [],
    sorters: sorters || [],
    filename: `order_report_${new Date().toISOString().slice(0, 10)}`,
    mapData: (item) => ({
      "Invoice Date": item.invoiceDate,
      "Invoice Currency": item.invoiceCurrency,
      "Order No": item.orderNo,
      "Vessel Name": item.vesselName,
      "IMO Number": item.imoNumber,
      "Shipco Name": item.shipcoName,
      "Order Date": item.orderDate,
      "Product Group": item.productGroup,
      "Product ID": item.productId,
      "UKHO Published Price": item.ukhoPublishedPrice,
      "UKHO Net Price": item.ukhoNetPrice,
      "Ordered By": item.orderedBy,
    }),
    pageSize: 10000,
  });

  function handleDateRangeSelection(value: any) {
    setDateSelected(true);
    searchFormProps?.form?.submit();
    searchFormProps?.form?.resetFields([
      "vesselName",
      "imoNumber",
      "shipcoName",
      "orderedBy",
    ]);
  }

  const columns: ColumnsType<IOrderReport> = [
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 130,
      className: "wrap-text",
      render: (value) => value ? <DateField format="DD-MMM-YYYY" value={value} /> : "",
      defaultSortOrder: getDefaultSortOrder("invoiceDate", sorters),
      sorter: true,
    },
    {
      title: "Invoice Currency",
      dataIndex: "invoiceCurrency",
      key: "invoiceCurrency",
      width: 100,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("invoiceCurrency", sorters),
      sorter: true,
    },
    {
      title: "Order No.",
      dataIndex: "orderNo",
      key: "orderNo",
      width: 100,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("orderNo", sorters),
      sorter: true,
    },
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 160,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("vesselName", sorters),
      sorter: true,
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 110,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("imoNumber", sorters),
      sorter: true,
    },
    {
      title: "Shipco Name",
      dataIndex: "shipcoName",
      key: "shipcoName",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("shipcoName", sorters),
      sorter: true,
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 130,
      className: "wrap-text",
      render: (value) => value ? <DateField format="DD-MMM-YYYY" value={value} /> : "",
      defaultSortOrder: getDefaultSortOrder("orderDate", sorters),
      sorter: true,
    },
    {
      title: "Product Group",
      dataIndex: "productGroup",
      key: "productGroup",
      width: 120,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("productGroup", sorters),
      sorter: true,
    },
    {
      title: "Product ID",
      dataIndex: "productId",
      key: "productId",
      width: 120,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("productId", sorters),
      sorter: true,
    },
    {
      title: "UKHO Published Price",
      dataIndex: "ukhoPublishedPrice",
      key: "ukhoPublishedPrice",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("ukhoPublishedPrice", sorters),
      sorter: true,
    },
    {
      title: "UKHO Net Price",
      dataIndex: "ukhoNetPrice",
      key: "ukhoNetPrice",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("ukhoNetPrice", sorters),
      sorter: true,
    },
    {
      title: "Ordered By",
      dataIndex: "orderedBy",
      key: "orderedBy",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("orderedBy", sorters),
      sorter: true,
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "Report",
        subTitle: "Orders",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <ExportButton
            onClick={triggerExport}
            disabled={!dateSelected}
            loading={exportLoading}
          />
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xl={24} lg={24} xs={24}>
          <Card type="inner" title={"Filter"}>
            <Filter
              formProps={searchFormProps}
              filters={filters || []}
              dateSelected={dateSelected}
              onDateRangeSelect={handleDateRangeSelection}
            />
          </Card>
        </Col>
        <Col xl={24} xs={24}>
          {dateSelected ? (
            <Table
              {...tableProps}
              columns={columns}
              rowKey="id"
              size="middle"
              sticky
              pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          ) : (
            <Space
              direction="vertical"
              size="small"
              style={{
                display: "flex",
              }}
            >
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}></Empty>
              <Alert
                message="Select Invoice Date"
                description="Please select an invoice date range to view the data"
                type="info"
                showIcon
                style={{
                  margin: "auto",
                  width: "40%",
                  padding: "15px",
                  textAlign: "center",
                }}
              />
            </Space>
          )}
        </Col>
      </Row>
    </List>
  );
};

const Filter: React.FC<{
  formProps: FormProps;
  filters: CrudFilters;
  dateSelected: boolean;
  onDateRangeSelect: any;
}> = (props) => {
  const { formProps, filters, dateSelected, onDateRangeSelect } = props;

  const { RangePicker } = DatePicker;

  const invoiceDate = React.useMemo(() => {
    const start = getDefaultFilter("invoiceDate", filters, "gte");
    const end = getDefaultFilter("invoiceDate", filters, "lte");

    const startFrom = dayjs(start);
    const endAt = dayjs(end);

    if (start && end) {
      return [startFrom, endAt];
    }
    return undefined;
  }, [filters]);

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        vesselName: getDefaultFilter("vesselName", filters),
        imoNumber: getDefaultFilter("imoNumber", filters),
        shipcoName: getDefaultFilter("shipcoName", filters),
        orderedBy: getDefaultFilter("orderedBy", filters),
        invoiceDate,
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item
            label={"Invoice Date"}
            name="invoiceDate"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <RangePicker
              style={{ width: "100%" }}
              onChange={onDateRangeSelect}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Vessel Name"} name="vesselName">
            <Input
              placeholder="filter by vessel name"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"IMO Number"} name="imoNumber">
            <Input
              placeholder="filter by IMO number"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Shipco"} name="shipcoName">
            <Input
              placeholder="filter by shipco"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Ordered By"} name="orderedBy">
            <Input
              placeholder="filter by ordered by"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item
            style={{
              margin: "auto",
              width: "25%",
            }}
          >
            <SaveButton
              onClick={formProps.form?.submit}
              size="large"
              block
              disabled={!dateSelected}
            >
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
